// Self-service onboarding shared constants (FDD-aligned).

export const JOURNEY_STAGES = [
  { key: "intro", label: "Start trial" },
  { key: "email", label: "Verify email" },
  { key: "org", label: "Organisation details" },
  { key: "goals", label: "Reporting goals" },
  { key: "summary", label: "Review & submit" },
];

// Full month names for financial-year selectors (value = month number as string).
export const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
].map((label, i) => ({ value: String(i + 1), label }));

// Clickable tooltips for the Financial Year & reporting-period step (Phase 2).
export const PERIOD_TIPS = {
  fy_end_month:
    "The month your financial year ends. Lestari aligns ESG reporting periods to this so your carbon and financial numbers cover the same window. Example: pick December if your accounts run January–December; pick March if your year ends 31 March.",
  base_year:
    "Your first reporting year — the starting line Lestari measures progress against. Example: choose 2024 to make FY2024 your baseline, then track reductions in 2025 and beyond.",
  cadence:
    "How often you'll capture data. More frequent means earlier anomaly detection and smoother reporting. Example: Monthly suits utility bills; Quarterly suits smaller teams; Annual is the lightest start.",
};

// Reporting-goal availability rules (Phase 1). Trial is always Lestari Lite.
export const LITE_LOCKED_GOALS = {
  climate_risk: "Climate-risk management is a Lestari Enterprise feature — not available on the Lite trial.",
  assurance: "Assurance readiness is a Lestari Enterprise feature — not available on the Lite trial.",
  board_visibility: "The board control tower is a Lestari Enterprise feature — not available on the Lite trial.",
};
export const LISTED_ONLY_GOALS = {
  sgx_reporting: "SGX reporting applies to SGX-listed companies. Select the SGX-Listed entity category to enable this.",
};
export const CARBON_TAX_GOAL = "carbon_tax";
export const CARBON_TAX_LOCK_REASON =
  "Carbon-tax tracking applies to companies in Carbon Pricing Act scope. Tick 'Carbon Pricing Act exposure' in your organisation details to enable this.";
// Selecting the baseline goal auto-pairs carbon-emissions tracking.
export const BASELINE_GOAL = "start_baseline";
export const EMISSIONS_GOAL = "track_emissions";

// Phase-2 provisioning wizard sub-steps (goals & package already captured / fixed to Lite).
export const PROVISION_WIZARD_STEPS = [
  {
    key: "period", title: "Financial year & reporting period",
    ai: "ESG reporting works best when your data periods align with your financial year. This also helps Lestari calculate timelines, reminders, and year-on-year comparisons.",
  },
  {
    key: "owners", title: "Configure ESG data owners",
    ai: "Good ESG reporting is a team sport. Assign the right people to the right data areas so data collection doesn't become a heroic spreadsheet rescue mission later.",
  },
  {
    key: "sources", title: "Select initial data sources",
    ai: "Start simple and improve over time. You don't need perfect integrations on day one — start with what you have, then automate later.",
  },
  {
    key: "baseline", title: "Baseline data setup",
    ai: "Your baseline is the starting line. Once Lestari has it, it can help you track improvement, set targets, and explain your progress credibly.",
  },
  {
    key: "materiality", title: "Review materiality recommendation",
    ai: "These topics are recommended based on your industry, company type, and reporting goals. Accept, defer, or customise them — high-priority topics are highlighted.",
  },
  {
    key: "review", title: "Review & activate your workspace",
    ai: "You're ready to activate your Lestari tenant. After this you'll get guided tasks and an onboarding dashboard to complete your ESG setup.",
  },
];

export const GOAL_OPTIONS = [
  { value: "start_baseline", label: "Start ESG baseline",
    tip: "Capture your first full year of ESG data so Lestari has a starting line to measure progress against." },
  { value: "sustainability_report", label: "Prepare sustainability report",
    tip: "Produce a structured sustainability/ESG report — Lestari assembles the data, narratives and disclosures for you." },
  { value: "supplier_questionnaire", label: "Respond to customer ESG questionnaire",
    tip: "Answer ESG questionnaires from customers, banks or buyers using verified data instead of ad-hoc spreadsheets." },
  { value: "sgx_reporting", label: "Prepare for SGX reporting",
    tip: "Get SGX-ready: Core ESG Metrics, climate disclosures and the filing pack listed issuers must submit." },
  { value: "ifrs_s2", label: "Prepare for IFRS S1/S2 disclosure",
    tip: "Prepare IFRS S1/S2 climate-related disclosures with readiness mapping and gap analysis." },
  { value: "track_emissions", label: "Track carbon emissions",
    tip: "Measure your Scope 1, 2 and 3 carbon footprint using Singapore emission factors." },
  { value: "climate_risk", label: "Manage climate risk",
    tip: "Identify and manage physical and transition climate risks with TCFD-aligned scenario analysis." },
  { value: "assurance", label: "Prepare for assurance",
    tip: "Build an audit-ready evidence trail so an external assurer can verify your numbers." },
  { value: "carbon_tax", label: "Track carbon tax exposure",
    tip: "Estimate your Carbon Pricing Act exposure and project future carbon-tax liability." },
  { value: "board_visibility", label: "Improve board visibility",
    tip: "Give your board and executives a live ESG control-tower view of performance and compliance." },
  { value: "financing_investor", label: "Support financing / investor reporting",
    tip: "Support green financing and investor reporting with credible, structured ESG data." },
];

export const DATA_SOURCE_OPTIONS = [
  { value: "manual", label: "Manual entry",
    tip: "Type figures straight into Lestari's forms — the simplest way to begin, no setup needed." },
  { value: "csv", label: "Excel / CSV upload",
    tip: "Upload Excel/CSV files; AI maps your columns to the right ESG fields automatically." },
  { value: "utility_bills", label: "Utility bills",
    tip: "Add electricity, water and gas usage from your SP Group / PUB bills." },
  { value: "fuel", label: "Fuel records",
    tip: "Capture diesel, petrol or gas used in vehicles, gensets and boilers from fuel receipts." },
  { value: "waste", label: "Waste contractor reports",
    tip: "Pull waste and recycling tonnages from your waste contractor's invoices or reports." },
  { value: "hr", label: "HR reports",
    tip: "Bring in headcount, training, turnover and safety data from your HR/payroll system." },
  { value: "supplier", label: "Supplier records",
    tip: "Collect emissions and ESG data from suppliers for your Scope 3 footprint." },
  { value: "travel", label: "Travel records",
    tip: "Add business travel (flights, ground transport) from expense claims or a travel tool." },
  { value: "erp", label: "ERP export",
    tip: "Export operational and financial data from your ERP (e.g. SAP) for richer metrics." },
  { value: "bms", label: "Building management data",
    tip: "Use building management system data for granular energy and occupancy figures." },
  { value: "smart_meter", label: "Smart meter data",
    tip: "Connect smart-meter readings for accurate, near-real-time electricity consumption." },
];

// Data-owner role areas → platform roles.
export const OWNER_ROLES = [
  { area: "ESG Manager", role: "esg_manager" },
  { area: "Finance owner", role: "approver" },
  { area: "Operations / Data owner", role: "data_officer" },
  { area: "HR owner", role: "data_officer" },
  { area: "Board / Executive viewer", role: "board" },
  { area: "Auditor / Viewer", role: "auditor" },
];

export const BASELINE_YEAR_TIP =
  "Pick the 12-month period you're capturing — usually your most recent completed financial year. This becomes your starting line, so Lestari can track your progress year on year. Don't overthink it; you can add earlier years later.";

export const BASELINE_FIELDS = [
  { key: "electricity_kwh", label: "Electricity (kWh)", group: "Environmental",
    tip: "Add up the kWh from your 12 monthly SP Group / utility bills for the year. Only have a few months? Average them and multiply up (e.g. average × 12) — a sensible estimate is a great start, and you can refine it anytime." },
  { key: "fuel_litres", label: "Fuel — diesel (litres)", group: "Environmental",
    tip: "Total litres of diesel/petrol used in gensets, vehicles or boilers over the year — check fuel receipts or your supplier's annual statement. No fuel on site? Just enter 0. A rough total beats a blank." },
  { key: "water_m3", label: "Water (m³)", group: "Environmental",
    tip: "Sum the cubic metres (m³) shown on your 12 PUB water bills. Missing a couple of months? Average what you have and scale to 12 — you're not aiming for perfection on day one." },
  { key: "waste_tonnes", label: "Waste generated (tonnes)", group: "Environmental",
    tip: "Estimate total waste sent for disposal/recycling in tonnes — your waste contractor's invoices usually show weights or the number of bin collections. A reasonable estimate is completely fine to begin with." },
  { key: "headcount", label: "Headcount", group: "Social",
    tip: "Your total number of employees (full-time equivalent), either at year-end or as a yearly average. Your HR or payroll system has this in seconds — an easy win!" },
  { key: "safety_incidents", label: "Safety incidents", group: "Social",
    tip: "The count of recordable workplace injuries/incidents for the year — 0 is a perfectly good (and great!) answer. Pull it from your incident log or WSH records." },
  { key: "training_hours", label: "Training hours", group: "Social",
    tip: "Total staff training hours across the year from your HR/L&D tracker. Only track sessions? Multiply sessions × hours × attendees. An approximate figure is absolutely okay." },
  { key: "revenue_sgd", label: "Revenue (SGD)", group: "Economic",
    tip: "Your total annual revenue in SGD from your management accounts or P&L. This lets Lestari calculate intensity metrics (e.g. emissions per S$ revenue) — round figures are fine." },
  { key: "floor_area_sqft", label: "Floor area (sq ft)", group: "Economic",
    tip: "The gross floor area your organisation occupies, in square feet — check your tenancy agreement or building handbook. Multiple sites? Simply add them together." },
  { key: "business_travel_km", label: "Business travel (km)", group: "Environmental",
    tip: "Approximate total business travel distance in km (flights + ground transport). No tracker yet? Estimate from expense claims or a few typical trips — start rough now and improve next cycle." },
];

export const ENTITY_CATEGORIES = [
  "SGX-Listed",
  "Large Non-Listed Company",
  "Mid-market / Non-Listed",
  "SME",
  "Government-Linked",
  "Public Sector",
  "Non-Profit",
];

// Provisioning wizard sub-steps (FDD §14.2, consolidated — identity/category/industry
// already confirmed in the profile stage).
export const WIZARD_STEPS = [
  {
    key: "goals",
    title: "Select reporting goals",
    why: "Understanding why you're using Lestari lets us switch on exactly the right workflows.",
    benefit: "Tailored modules, no clutter.",
    ai: "Your goals help Lestari configure the right workflows. For example, if you need assurance readiness, we'll turn on stronger evidence tracking and audit-trail features.",
  },
  {
    key: "period",
    title: "Financial year & reporting period",
    why: "ESG data works best when aligned to your financial year.",
    benefit: "Accurate timelines, reminders and year-on-year comparisons.",
    ai: "ESG reporting works best when your data periods align with your financial year. This also helps Lestari calculate timelines, reminders, and year-on-year comparisons.",
  },
  {
    key: "owners",
    title: "Configure ESG data owners",
    why: "Assign the right people to the right data areas.",
    benefit: "No last-minute spreadsheet rescue missions.",
    ai: "Good ESG reporting is a team sport. This step helps you assign the right people to the right data areas so that data collection doesn't become a heroic spreadsheet rescue mission later.",
  },
  {
    key: "sources",
    title: "Select initial data sources",
    why: "Start with whatever data you already have.",
    benefit: "Begin simple today, automate later.",
    ai: "Lestari can help you start simple and improve over time. You don't need perfect integrations on day one. Start with what you have, then automate later.",
  },
  {
    key: "baseline",
    title: "Baseline data setup",
    why: "Your baseline is the starting line for tracking progress.",
    benefit: "Credible targets and progress narratives.",
    ai: "Your baseline is the starting line. Once Lestari has your baseline, it can help you track improvement, set targets, and explain your progress credibly.",
  },
  {
    key: "materiality",
    title: "Review materiality recommendation",
    why: "Focus on the issues that matter most for your industry.",
    benefit: "A right-sized, defensible ESG agenda.",
    ai: "These topics are recommended based on your industry, company type, and reporting goals. You can accept, defer, or customise them. Mandatory or high-priority topics are highlighted.",
  },
  {
    key: "review",
    title: "Review & activate tenant",
    why: "Confirm your configuration and go live.",
    benefit: "A provisioned tenant with a 15-day trial.",
    ai: "You're ready to activate your Lestari tenant. After this, your team will receive access, guided tasks, and an onboarding dashboard to help you complete your ESG setup.",
  },
];

export const TOUR_STEPS = [
  { title: "Welcome to Lestari", body: "Your AI-powered ESG, climate-risk and compliance control tower. This quick tour shows you around your new workspace." },
  { title: "Your organisation profile", body: "Your verified Singapore entity, regulatory tier and obligations are pinned to the top — everything stays tied to the right legal entity." },
  { title: "Your ESG setup checklist", body: "The setup completion score tracks what's done and what's next. Tick items off to reach 100%." },
  { title: "Your materiality recommendations", body: "Industry-tailored material topics drive your KPI library and data-collection tasks." },
  { title: "Your baseline data workspace", body: "Capture electricity, fuel, water, headcount and more — your starting line for tracking improvement." },
  { title: "Your dashboards", body: "Trial status, verification, data collection and compliance readiness, all at a glance." },
  { title: "Your AI adviser", body: "Ask 'What should I do next?' anytime — your adviser explains the why and recommends the next best action." },
  { title: "Your trial & package", body: "You're on a 15-day trial. Upgrade to a paid package whenever you're ready to unlock everything." },
];
